/** @jsxImportSource @emotion/react */
import React, { useMemo } from "react";
import theme from "@styles/theme";
import { useSelector } from "react-redux";
import { format, startOfDay, isSameDay } from "date-fns";
import { css } from "@emotion/react";
import { getLanguage, capitalizeFirstLetter } from "@helpers";
import { selectWeekOverview } from "@store/dayReport/dayReportSelector";
import { selectAllRateTypes } from "@store/rateType/rateTypeSelectors";
import { overrideTableStyles } from "@components/base/table/overrides";
import Table from "@components/base/table";
import { useTranslation } from "react-i18next";
import useAppSelector from "@hooks/useAppSelector";

const ForecastTable: React.FC = () => {
  const { t } = useTranslation();
  const dayReports = useSelector(selectWeekOverview);

  const dayReportsArray = useMemo(
    () =>
      !!dayReports
        ? Object.values(dayReports).sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
          )
        : [],
    [dayReports],
  );
  const rateTypes = useAppSelector(({ rateType }) => {
    if (!rateType) return [];
    return selectAllRateTypes(rateType);
  });

  const today = useMemo(() => startOfDay(new Date()), []);

  const isHistorical = (date: string) => startOfDay(new Date(date)) <= today;

  const pastColumnCount = useMemo(() => {
    const todayIndex = dayReportsArray.findIndex((report) =>
      isSameDay(new Date(report.date), today),
    );
    if (todayIndex !== -1) return todayIndex + 2; // +2 for label column and 1-based CSS nth-child

    const lastDate = dayReportsArray.at(-1);
    if (lastDate && new Date(lastDate.date) < today) {
      return dayReportsArray.length + 1; // +1 for label column
    }
    return null;
  }, [dayReportsArray]);

  const getRateTypeNames = (): string[] => {
    const rateNames = dayReportsArray.flatMap((report) =>
      report.forecast?.perRate ? Object.keys(report.forecast.perRate) : [],
    );

    // If no forecast data, use rate types from store
    if (rateNames.length === 0 && rateTypes.length > 0) {
      return rateTypes.map((rt) => rt.name).sort();
    }

    return [...new Set(rateNames)].sort();
  };

  const generateDayColumns = () => {
    return dayReportsArray.map((report) =>
      format(new Date(report.date), "eeeeee d MMM", {
        locale: getLanguage(),
      }),
    );
  };

  const generateForecastRows = () => {
    const forecastRows = [];
    const rateTypeNames = getRateTypeNames();
    const getRateTypeLabel = (rateName: string) => {
      const normalizedRateName = rateName.trim().toLocaleLowerCase();
      if (["kamers", "rooms"].includes(normalizedRateName)) {
        return t("forecast.rooms");
      }
      if (["suites"].includes(normalizedRateName)) {
        return t("forecast.suites");
      }
      return capitalizeFirstLetter(rateName);
    };

    // Show each rate type as a row (Rooms, Suites, etc.)
    rateTypeNames.forEach((rateName) => {
      forecastRows.push([
        <p key={`${rateName}-label`}>
          <strong css={styles.bold}>{getRateTypeLabel(rateName)}</strong>
          <br />
          {t("forecast.occupiedLeft")}{" "}
          <span css={styles.lighter}>/ {t("forecast.availableRight")}</span>
        </p>,
        ...dayReportsArray.map((report) => {
          const rateData = report.forecast?.perRate?.[rateName];
          const historical = isHistorical(report.date);
          return (
            <span key={`${rateName}-${report.date}`}>
              {historical ? "-" : (rateData?.occupied ?? "-")}
              <span css={styles.lighter}>
                {" "}
                / {historical ? "-" : (rateData?.available ?? "-")}
              </span>
            </span>
          );
        }),
      ]);
    });

    // Add arrivals row
    forecastRows.push([
      <p key="arrivals-label">
        <strong css={styles.bold}>{t("forecast.expectedArrivals")}</strong>
        <br />
        <span>{t("forecast.totalRoomsLeft")} </span>
        <span css={styles.lighter}>/ {t("forecast.totalGuestsRight")}</span>
      </p>,
      ...dayReportsArray.map((report) => {
        const arr = report.forecast?.arrivals;
        const historical = isHistorical(report.date);
        return (
          <span key={`arrivals-${report.date}`}>
            {historical ? "-" : (arr?.rooms ?? "-")}
            <span css={styles.lighter}>
              {" "}
              / {historical ? "-" : (arr?.guests ?? "-")}
            </span>
          </span>
        );
      }),
    ]);

    // Add departures row
    forecastRows.push([
      <p key="departures-label">
        <strong css={styles.bold}>{t("forecast.expectedDepartures")}</strong>
        <br />
        {t("forecast.totalRoomsLeft")}{" "}
        <span css={styles.lighter}>/ {t("forecast.totalGuestsRight")}</span>
      </p>,
      ...dayReportsArray.map((report) => {
        const dep = report.forecast?.departures;
        const historical = isHistorical(report.date);
        return (
          <span key={`departures-${report.date}`}>
            {historical ? "-" : (dep?.rooms ?? "-")}
            <span css={styles.lighter}>
              {" "}
              / {historical ? "-" : (dep?.guests ?? "-")}
            </span>
          </span>
        );
      }),
    ]);

    // Add stayovers row
    forecastRows.push([
      <p key="stayovers-label">
        <strong css={styles.bold}>{t("forecast.expectedStayovers")}</strong>
        <br />
        {t("forecast.totalRoomsLeft")}{" "}
        <span css={styles.lighter}>/ {t("forecast.totalGuestsRight")}</span>
      </p>,
      ...dayReportsArray.map((report) => {
        const stay = report.forecast?.stayovers;
        const historical = isHistorical(report.date);
        return (
          <span key={`stayovers-${report.date}`}>
            {historical ? "-" : (stay?.rooms ?? "-")}
            <span css={styles.lighter}>
              {" "}
              / {historical ? "-" : (stay?.guests ?? "-")}
            </span>
          </span>
        );
      }),
    ]);

    // Add total to clean row
    forecastRows.push([
      <p key="totalToClean-label">
        <strong css={styles.bold}>{t("forecast.totalToClean")}</strong>
      </p>,
      ...dayReportsArray.map((report) => {
        return (
          <strong key={`totalToClean-${report.date}`}>
            {isHistorical(report.date)
              ? "-"
              : (report.forecast?.totalToClean ?? "-")}
          </strong>
        );
      }),
    ]);

    return forecastRows;
  };

  return (
    <Table
      columns={[null, ...generateDayColumns()]}
      data={generateForecastRows()}
      overrides={overrideTableStyles({
        Root: {
          style: {
            overflow: "auto",
          },
        },
        TableBodyRow: {
          style: {
            whiteSpace: "nowrap",
          },
          ":nth-child(n)": {
            backgroundColor: `${theme.colors.white}`,
          },
          ":nth-child(2n)": {
            backgroundColor: `${theme.colors.mono200}`,
          },
        },
        TableHeadCell: {
          ...(pastColumnCount && {
            [`:nth-child(-n+${pastColumnCount}):nth-child(n+2)`]: {
              opacity: 0.25,
            },
          }),
        },
        TableBodyCell: {
          ...(pastColumnCount && {
            [`:nth-child(-n+${pastColumnCount}):nth-child(n+2)`]: {
              opacity: 0.25,
            },
          }),
        },
      })}
    />
  );
};

const styles = {
  bold: css({
    fontWeight: 600,
  }),
  lighter: css({
    opacity: 0.6,
  }),
};

export default ForecastTable;
